import { request } from 'api';
import { endpoints } from 'api/endpoints';
import { ReactComponent as DropdownArrowIcon } from 'assets/dropdown-arrow.svg';
import { EmptyChatIcon, FileDownloadIcon } from 'assets/icons';
import { AxiosError } from 'axios';
import classNames from 'classnames';
import { format } from 'date-fns';
import { ForbiddenErrorResponse } from 'pages/OrderDetails/OrderDetailsForSubOrEx/components/types';
import { FC, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { OrderFile } from 'types';
import UploadInput from 'ui/UploadInput';
import { editChatRuLocale } from 'utils/helpers';
import { EMPTY_MESSAGE_TEXT_ERROR, EMPTY_MESSAGE_TOAST_ERROR } from '../consts';
import { ChatsWithFilesV2 } from '../types';
import { OpenedChatProps } from './types';
import { Message } from './Message';

export const OpenedChat: FC<OpenedChatProps> = ({
  state,
  uploadedFiles,
  orderId,
  setAll,
  currentUserId,
  isSmall,
  setOpen,
  customId,
  entityType,
  timeZone,
  isMessageCanSent,
  lastMessageRef,
  setUploadedFiles,
}) => {
  let lastDate = '';
  const { chats } = state;
  const [mobileChats, setMobileChats] = useState(chats);
  const mobileTextArea = useRef<HTMLTextAreaElement>(null);
  const desctopTextArea = useRef<HTMLTextAreaElement>(null);
  const [mobileUploadedFiles, setMobileUploadedFiles] = useState<OrderFile[] | undefined>(
    undefined
  );
  const [showMobileErrorMessage, setShowMobileErrorMessage] = useState<string>('');

  const sendChatMessage = async () => {
    if (!desctopTextArea.current?.value.trim().length && uploadedFiles?.length) {
      toast.error(EMPTY_MESSAGE_TOAST_ERROR);
      return;
    }
    if (!desctopTextArea.current?.value.trim().length) {
      toast.error(EMPTY_MESSAGE_TEXT_ERROR);
      return;
    }
    const messageCopy = desctopTextArea.current.value.trim();
    const reqBody = {
      content: desctopTextArea.current.value.trim(),
      files: uploadedFiles,
    };
    desctopTextArea.current.value = '';
    try {
      const response = await request<ChatsWithFilesV2>(
        endpoints.CHAT_ORDER_SEND_MESSAGE.type,
        endpoints.CHAT_ORDER_SEND_MESSAGE.url(orderId),
        { data: reqBody }
      );
      if (response.status === 200) {
        setAll(response.data);
        setUploadedFiles(undefined);
      } else {
        desctopTextArea.current.value = messageCopy;
      }
    } catch (error) {
      toast.error(
        (error as AxiosError<ForbiddenErrorResponse>).response?.data?.detail ||
          'Произошла ошибка, обратитесь к администратору'
      );
      desctopTextArea.current.value = messageCopy;
    }
  };

  const sendMChatMessage = async () => {
    if (mobileTextArea.current) {
      if (mobileTextArea.current.value.trim().length === 0 && uploadedFiles) {
        setShowMobileErrorMessage(EMPTY_MESSAGE_TEXT_ERROR);
        return;
      }
      if (mobileTextArea.current.value.trim().length === 0) {
        setShowMobileErrorMessage(EMPTY_MESSAGE_TEXT_ERROR);
        return;
      }
    }
    const messageCopy = mobileTextArea.current;
    let reqBody = {};
    if (mobileTextArea.current) {
      reqBody = {
        content: mobileTextArea.current.value.trim(),
        files: mobileUploadedFiles,
      };
    }
    if (mobileTextArea.current) {
      mobileTextArea.current.value = '';
    }
    const response = await request<ChatsWithFilesV2>(
      endpoints.CHAT_ORDER_SEND_MESSAGE.type,
      endpoints.CHAT_ORDER_SEND_MESSAGE.url(orderId),
      { data: reqBody }
    );
    if (response.status === 200) {
      setAll(response.data);
      setMobileUploadedFiles(undefined);
      setMobileChats(response.data);
    } else {
      if (mobileTextArea.current && messageCopy) {
        mobileTextArea.current.value = messageCopy.value;
      }
    }
  };

  const ruLocale = editChatRuLocale();

  return (
    <div className="relative h-auto w-screen max-w-[640px] self-end rounded-t-xl bg-white sm:w-full sm:px-4 sm:shadow-chatPanel md:m-0">
      <>
        {currentUserId ? (
          <>
            {!isSmall && (
              <div className="absolute -left-9 flex h-8 w-8 items-center justify-center rounded-2xl border text-text-50">
                <DropdownArrowIcon
                  className="h-4 w-4 cursor-pointer text-text-50"
                  onClick={() => setOpen(false)}
                />
              </div>
            )}
            {!isSmall && (
              <div
                className="title2-medium sm:headline-bold ml-4 mb-2 mt-12 flex cursor-pointer items-center gap-2 py-2 px-[12px] text-text-100 sm:mx-0 sm:mt-0 sm:ml-0"
                onClick={() => setOpen(false)}
              >
                <p>{customId ? `Чат по заявке № ${customId}` : 'Чат по заявке'}</p>
              </div>
            )}
            {isSmall ? (
              <>
                {mobileChats &&
                mobileChats.messages.length > 0 &&
                mobileChats.memberships.length > 0 ? (
                  <div className="mt-4 flex h-auto min-h-chatMobile w-screen flex-col overflow-x-hidden bg-white px-4 pb-4 sm:mt-0 sm:h-96 sm:min-h-full sm:w-full sm:px-2 sm:pt-2 sm:pb-4">
                    <div
                      className={classNames('flex flex-col gap-2 overflow-x-auto sm:mb-0', {
                        'mb-24': uploadedFiles?.length,
                      })}
                      id="message-container"
                    >
                      {mobileChats.messages.map(({ created, userId, content, files }) => {
                        const date = format(new Date(created), 'dd.MM.yyyy');
                        let showDate;
                        if (!lastDate || lastDate !== date) {
                          lastDate = date;
                          showDate = true;
                        } else {
                          showDate = false;
                        }
                        return (
                          <div key={created}>
                            {showDate && (
                              <div className="leading-cz relative my-4 text-xs">
                                <span className="absolute top-1/2 right-1/2 translate-x-1/2 -translate-y-1/2 rounded-3xl bg-light-30 py-1 px-3 text-text-100">
                                  {format(new Date(created), 'd MMMM, yyyy', {
                                    locale: ruLocale,
                                  })}
                                </span>
                                <hr className="text-white" />
                              </div>
                            )}
                            <Message
                              userId={userId}
                              content={content}
                              created={created}
                              files={files}
                              state={state}
                              currentUserId={currentUserId}
                              entityType={entityType}
                              timeZone={timeZone}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="h-auto min-h-[80vh] overflow-x-hidden px-2 pt-2 pb-4 sm:h-96 sm:min-h-full">
                    <div
                      className="flex min-h-[75vh] flex-col items-center justify-center sm:min-h-full"
                      id="message-container"
                    >
                      <EmptyChatIcon />
                      <h3 className="headline-bold text-text-100">Привет, это - чат!</h3>
                    </div>
                  </div>
                )}
                {isMessageCanSent && (
                  <div className={classNames('relative ml-6 flex flex-col bg-white sm:ml-0', {})}>
                    {mobileUploadedFiles &&
                    showMobileErrorMessage &&
                    mobileUploadedFiles.length > 0 ? (
                      <span className="mb-3 text-danger">{showMobileErrorMessage}</span>
                    ) : (
                      <span className="-mb-1 text-danger">{showMobileErrorMessage}</span>
                    )}
                    <UploadInput
                      name={'name'}
                      fileBusinessTypeId={6}
                      onChange={setMobileUploadedFiles}
                      label={'label'}
                      maxFileSize={10}
                      value={mobileUploadedFiles ? mobileUploadedFiles : undefined}
                      isChat={true}
                    />
                    <textarea
                      className="leading-cz callout-normal my-1 mx-10 resize-none overflow-auto rounded-xl border-t border-gray bg-light-30 p-3 pr-10 outline-none sm:w-4/5"
                      autoFocus
                      ref={mobileTextArea}
                      rows={1}
                      placeholder="Сообщение"
                      onChange={(e) => {
                        setShowMobileErrorMessage('');
                        if (mobileTextArea.current) {
                          mobileTextArea.current.value = e.target.value;
                        }
                      }}
                    />
                    <button
                      onClick={sendMChatMessage}
                      className="cursor-pointe absolute right-3 bottom-4"
                      type="button"
                    >
                      <FileDownloadIcon className="rotate-180 text-primary-60" />
                    </button>
                  </div>
                )}
                <div className="lastMessage" ref={lastMessageRef} />
              </>
            ) : (
              // DESKTOP
              <>
                {chats && chats.messages.length > 0 && chats.memberships.length > 0 ? (
                  <div className="flex max-h-96 flex-col overflow-x-hidden bg-white px-4 pb-4 sm:px-2 sm:pt-2 sm:pb-4">
                    <div
                      className={classNames('flex flex-col gap-2 overflow-x-auto sm:mb-0', {
                        'mb-24': uploadedFiles?.length,
                      })}
                      id="message-container"
                    >
                      {chats.messages.map(({ created, userId, content, files }) => {
                        const date = format(new Date(created), 'dd.MM.yyyy');
                        let showDate;
                        if (!lastDate || lastDate !== date) {
                          lastDate = date;
                          showDate = true;
                        } else {
                          showDate = false;
                        }
                        return (
                          <div key={created}>
                            {showDate && (
                              <div className="leading-cz relative my-4 text-xs">
                                <span className="absolute top-1/2 right-1/2 translate-x-1/2 -translate-y-1/2 rounded-3xl bg-light-30 py-1 px-3 text-text-100">
                                  {format(new Date(created), 'd MMMM, yyyy', {
                                    locale: ruLocale,
                                  })}
                                </span>
                                <hr className="text-white" />
                              </div>
                            )}
                            <Message
                              userId={userId}
                              content={content}
                              created={created}
                              files={files}
                              state={state}
                              currentUserId={currentUserId}
                              entityType={entityType}
                              timeZone={timeZone}
                            />
                          </div>
                        );
                      })}
                      <div className="lastMessage" ref={lastMessageRef} />
                    </div>
                  </div>
                ) : (
                  <div className="h-auto min-h-[80vh] overflow-x-hidden px-2 pt-2 pb-4 sm:h-96 sm:min-h-full">
                    <div
                      className="flex min-h-[75vh] flex-col items-center justify-center sm:min-h-full"
                      id="message-container"
                    >
                      <EmptyChatIcon />
                      <h3 className="headline-bold text-text-100">Привет, это - чат!</h3>
                    </div>
                  </div>
                )}
                {isMessageCanSent && (
                  <div className={classNames('relative ml-6 flex flex-col bg-white sm:ml-0', {})}>
                    <UploadInput
                      name={'name'}
                      fileBusinessTypeId={6}
                      onChange={setUploadedFiles}
                      label={'label'}
                      maxFileSize={10}
                      value={uploadedFiles ? uploadedFiles : undefined}
                      isChat={true}
                    />
                    <textarea
                      className="leading-cz callout-normal my-1 mx-10 resize-none overflow-auto rounded-xl border-t border-gray bg-light-30 p-3 pr-10 outline-none sm:w-4/5"
                      autoFocus
                      rows={1}
                      placeholder="Введите сообщение"
                      onChange={(e) => {
                        if (desctopTextArea.current) {
                          desctopTextArea.current.value = e.target.value;
                        }
                      }}
                      ref={desctopTextArea}
                    />
                    <button
                      onClick={sendChatMessage}
                      className="cursor-pointe absolute right-0 bottom-4"
                      type="button"
                    >
                      <FileDownloadIcon className="rotate-180 text-primary-60" />
                    </button>
                  </div>
                )}
              </>
            )}
          </>
        ) : (
          ''
        )}
      </>
    </div>
  );
};
